import { EventEmitter } from "@angular/core";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { environment } from "../../environments/environment";
import { RuleAction } from "../enum";
import { Utilities } from "../helpers/utilities";
import { Value } from "../models/BingImageResult";
import { InspirationRoomOptionModel } from "../models/InspirationRoomOptionModel";
import { InspirationRoomTipsModel } from "../models/InspirationRoomTipsModel";
import { OptionTag } from "../models/OptionTagModel";
import { TagFilter } from "../models/TagFilter";
import { TagGroupModel } from "../models/TagGroupModel";

import { Appointment, Area, Option, Rule, StyleChoice, StyleSelection, CFAOptions, Status } from "./models/models";

export class OptionEngine {
  mediaAssociations: Value[];
  public optionDefinitions: Option[];
  optionTags: OptionTag[];
  tips: InspirationRoomTipsModel[];
  tagGroups: TagGroupModel[];
  tagFilter: TagFilter[];
  status: Status[];
  displayedOptions: Option[];
  selectionActions: Option[] = [];
  inspirationOptionIds: number[];
  appointmentDetails: Appointment;
  exceededBudgetAmount: number;
  cfaOptions: CFAOptions[];
  selectedOptions: Option[] = [];
  public isSaving = false;
  public isDirty = false;
  public isDirtyWhileSaving = false;
  selectedOptionChanged: EventEmitter<Option> = new EventEmitter;
  optionRemoved: EventEmitter<Option> = new EventEmitter;
  selectionUpdated: EventEmitter<Option> = new EventEmitter;

  constructor(private $gaService: GoogleAnalyticsService) { }

  onOptionChanged() {
    return this.selectedOptionChanged;
  }
  onOptionRemoved() {
    return this.optionRemoved;
  }
  onSelectionUpdated() {
    return this.selectionUpdated;
  }

  tagToDisplayName(tag: string): string {
    if (tag === "All") { return tag; }
    const displayName = this.tagGroups.filter(q => q.tagName === tag)[0].displayName;
    return displayName;
  }

  toggleLiked(option: Option): boolean {
    option.isLiked = !option.isLiked;

    // Cascade and unset any likes that are within this group (if unliked)
    if (!option.isLiked) {
      if (option.styleSelections?.length) {
        for (let styleSelection of option.styleSelections) {
          for (let styleChoice of styleSelection.styleChoices) {
            styleChoice.isLiked = false;
          }
        }
      }
    }

    this.setDirty();
    return option.isLiked;
  }

  toggleLikedStyle(styleChoice: StyleChoice, parentOption: Option): boolean {
    styleChoice.isLiked = !styleChoice.isLiked;
    if (styleChoice.isLiked) {
      parentOption.isLiked = true;
    }
    this.setDirty();
    return styleChoice.isLiked;
  }

  saveOptionsToLocalStorage() {
    //localStorage.setItem("options", JSON.stringify(this.optionDefinitions));
  }

  private _allOptionsAndStyleIds: number[] = [];

  allOptionAndStyleIds() {
    // If the collection already exists, don't re-enumerate it.
    if (this._allOptionsAndStyleIds && this._allOptionsAndStyleIds?.length) { return this._allOptionsAndStyleIds; }

    // Recursively look at every option, child option, and style choice
    for (const option of this.optionDefinitions) {
      this.extractOptionIds(option);
    }

    return this._allOptionsAndStyleIds;
  }

  extractOptionIds(option: Option) {
    if (!this._allOptionsAndStyleIds.includes(option.optionId)) {
      let isAdmin = JSON.parse(localStorage.getItem('userType')).isAdmin;
      if (option.isHiddenByDefault) {
        if (!isAdmin) {
          return;
        }
      }

      this._allOptionsAndStyleIds.push(option.optionId);

      if (option.styleSelections && option.styleSelections.length) {
        for (const style of option?.styleSelections) {
          for (const choice of style?.styleChoices) {
            this._allOptionsAndStyleIds.push(choice.optionId);
          }
        }
      }

      if (option.childOptions && option.childOptions.length) {
        for (const childOption of option.childOptions) {
          this.extractOptionIds(childOption);
        }
      }
    }
  }

  rulesByOption(rules: Rule[], action: number): Rule[] {
    const ruleMatches = rules.filter(q => q.action === action);
    const validRules: Rule[] = [];

    if (action === 0) // Remove
    {
      if (ruleMatches?.length) {
        for (const rule of ruleMatches) {
          const match = this.displayedOptions.filter(q => q.optionId == rule.optionId);
          if (match?.length) {
            const option = match[0];
            if (option.isVisible || option.isSelected) {
              validRules.push(rule);
            }
          }
        }
      }
      return validRules;
    }
    return ruleMatches;
  }

  loadOptionTags(optionTags: OptionTag[]) {
    this.optionTags = optionTags;
    for (const optionTag of this.optionTags) {
      if (optionTag?.options?.length) {
        const options = optionTag.options.split(',');
        optionTag.optionIds = [];
        for (const option of options) {
          optionTag.optionIds.push(+option);
        }
      }
    }
  }

  loadOptionSchema(options: Option[]): boolean {
    this.initStatus();
    if (options && options.length) {
      this.optionDefinitions = options;
      this.prepareAllOptionsForSelection();
      this.filterAreaOptions();
      this.saveOptionsToLocalStorage();
      this.updateCalculations();
      return true;
    }
    return false;
  }

  initStatus() {
    this.status = [];

    let statusRecord = {
      id: 1,
      status: "IN PROGRESS"
    };
    this.status.push(statusRecord);
    statusRecord = {
      id: 2,
      status: "COMPLETED"
    };
    this.status.push(statusRecord);
  }

  filterAreaOptions() {
    this.displayedOptions = this.baseAreaOptions();

    // Repeat any action (rule applications by user in app)
    if (this.selectionActions.length > 0) {
      for (const option of this.selectionActions) {
        // Add the option to display options if it doesn't already exist
        if (!this.displayedOptions.filter(q => q.optionId === option.optionId).length) {
          if (!option.selectedOption) {
            this.prepareOption(option);
          }

          this.displayedOptions.push(option);
        }

        if (option.rules) {
          this.applyRulesForOption(option);
        }
      }
    }
    this.displayedOptions = this.displayedOptions.filter(q => q.isVisible) //.sort((a, b) => (a.price > b.price) ? 1 : -1);
  }

  applyRulesForOption(option: Option) {
    if (!option.rules) {
      return;
    }

    for (const rule of option?.rules) {
      let targetOption = this.optionDefinitions.find(q => q.optionId === rule.optionId);

      //if (targetOption.isPackage) {
      //  targetOption.selectedOption = targetOption;
      //}

      if (targetOption) {
        // Apply the original rule
        if (rule.action == RuleAction.Add) {
          console.log(`(Rule) Adding ${targetOption.optionId}`);

          targetOption.isVisible = true;
          targetOption.isHiddenByDefault = false;
          targetOption.ruleSourceOptionId = option.optionId;
          // Add the option if it is included / $0
          if (targetOption.price === 0) {
            targetOption.isAdded = true;
            if (targetOption.childOptions && targetOption.childOptions.length === 1) {
              targetOption.selectedOption = targetOption.childOptions[0];
            } else if (targetOption.childOptions && targetOption.childOptions.some(x => x.isDefault || x.isSelected || (targetOption.includedFeatureOptionId == x.optionId))) {
              targetOption.selectedOption = targetOption.childOptions.find(x => x.isDefault || x.isSelected || (targetOption.includedFeatureOptionId == x.optionId));
            } else {
              //targetOption.selectedOption = targetOption;
            }
          }
        } else if (rule.action == RuleAction.Remove) {
          console.log(`(Rule) Removing ${targetOption.optionId}`);

          targetOption.isVisible = false;
          targetOption.isSelected = false;
          targetOption.isAdded = false;
          targetOption.isHiddenByRule = true;
        }

        // Reverse the rule if the option isn't selected
        if (!option.isSelected && !option.isClientSelected) {
          let revertRuleAction = rule.action == RuleAction.Add ? RuleAction.Remove : RuleAction.Add;
          if (revertRuleAction == RuleAction.Add) {
            if (targetOption.isHiddenByRule && !targetOption.isHiddenByDefault) {
              targetOption.isVisible = true;
              targetOption.isHiddenByRule = false;
            }
            targetOption.isHiddenByRule = false;
          } else if (revertRuleAction == RuleAction.Remove) {
            targetOption.isVisible = false;
            targetOption.isAdded = false;
            targetOption.isSelected = false;
            targetOption.isHiddenByDefault = true;
            if (targetOption.hasOwnProperty("selectedOption")) {
              delete (targetOption.selectedOption);
            }
            targetOption.isAdded = false;
          }
          //targetOption.isVisible = !targetOption.isVisible;
          //targetOption.isHiddenByDefault = !targetOption.isHiddenByDefault;
        }

        if (targetOption.selectedOption) {
          this.updateCalculations();
        }
      }
    }

    this.displayedOptions = this.baseAreaOptions();
  }

  applyExclusiveRulesForOption(option: Option) {
    const exclusiveRulesOptions = option.exclusiveRules.split(',');
    exclusiveRulesOptions.forEach(exclusiveOption => {
      if (Number(exclusiveOption) !== option.optionId) {
        const parentOption = this.optionDefinitions.filter(q => q.optionId === Number(exclusiveOption))[0];
        parentOption.isAdded = false;
        parentOption.isClientSelected = false;
        parentOption.selectedOption = null;
        parentOption.isStyleSelected = true;
        parentOption.quantity = 1;
        if (parentOption.childOptions) {
          parentOption.childOptions.map(q => q.isSelected = false);
          if (parentOption.isRequired) {
            // If no option exists, but an included feature exists at $0 price, automatically select it.
            const firstIncludedOption = parentOption.childOptions.find(x => x.price === 0);
            parentOption.selectedOption = firstIncludedOption;
            firstIncludedOption.isSelected = true;
            parentOption.isSelected = true;
          }
        }
      }
    });
  }

  addRemoveOptionalOption(option: Option, event) {
    this.setDirty();
    const parentOption = option;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (option.childOptions && option.childOptions.length === 1) {
      //option.childOptions[0].isAdded = !option.childOptions[0].isAdded
      option = option.childOptions[0];
    }

    if (option.selectedOption && option.selectedOption !== null) {
      option.selectedOption = null;
      option.isAdded = true;
    }

    option.isAdded = !option.isAdded;

    if (option.isAdded) {
      option.isClientSelected = true;
      parentOption.selectedOption = option;
      this.checkStyleSelections(option);
      // Automatically like any option that's added.
      option.isLiked = true;
      if (option.exclusiveRules) {
        this.applyExclusiveRulesForOption(option);
      }
    }
    else {
      option.isClientSelected = false;
      parentOption.selectedOption = null;
      option.isStyleSelected = true;
      option.quantity = 1;
      option.selectedOption = null;
    }
    if (option.price) {
      //this.calculateOptionalPrice(option)
      this.updateCalculations();
      option.oldQuantity = option.quantity;
    }

    if (option.selectedOption) {
      if (option.selectedOption.rules) {
        this.applyRulesForOption(option.selectedOption);
      }
    }
    else {
      if (option.rules) {
        this.applyRulesForOption(option);
      }
    }
    this.selectionUpdated.emit(parentOption);
  }

  resetStyles(parentOptionId: number) {
    const parentOption = this.optionDefinitions.find(q => q.optionId === parentOptionId);
    for (const child of parentOption.childOptions) {
      if (child.optionId == parentOption.selectedOption.optionId && child.styleSelections) {
        for (const colorList of child.styleSelections) {
          if (colorList.styleChoices) {
            for (const style of colorList.styleChoices) {
              style.isSelected = false;
            }
          }
        }
      }
    }
    parentOption.styleSelections = null;

    // Update Gallery
    parentOption.selectedPictureGallery = [];
    parentOption.selectedPictureGallery = this.getSelectedOptionPictures(parentOption);
    this.updateGalleryPicture(parentOption);
    this.checkStyleSelections(parentOption);
  }

  selectOption(parentOptionId: number,
    childOptionId: number,
    styleParentId: number,
    styleChoiceId: number,
    isLiked: boolean) {
    this.setDirty();

    const parentOption = this.optionDefinitions.find(q => q.optionId === parentOptionId);

    let oldSelectedOption: Option;
    let oldOptionPrice = 0;
    if (parentOption.selectedOption) {
      oldOptionPrice = parentOption.selectedOption.price * parentOption.selectedOption.quantity;
      oldSelectedOption = parentOption.selectedOption;
      oldSelectedOption.isClientSelected = false;
    }

    //remove style selection for old selected child
    if (!styleChoiceId && !styleParentId && parentOption.selectedOption && parentOption.childOptions) {
      this.resetStyles(parentOptionId);
    }

    // Remove not required options.
    if (!parentOption.isRequired && parentOption.selectedOption && !styleChoiceId) {
      if (parentOption.selectedOption.optionId === childOptionId) {
        parentOption.selectedOption = null;
        // this.calculatePrice(oldOptionPrice, 0);
        parentOption.childOptions.map(q => q.isSelected = false);
        parentOption.childOptions.map(q => q.isAdded = false);
        parentOption.childOptions.map(q => q.isClientSelected = false);
        parentOption.styleSelections = null;
        if (oldSelectedOption?.rules) {
          this.applyRulesForOption(oldSelectedOption);
        }
        parentOption.selectedPictureGallery = [];
        parentOption.selectedPictureGallery = this.getSelectedOptionPictures(parentOption);

        if (!parentOption.selectedPictureGallery.length && parentOption.defaultGalleryPicture) {
          parentOption.selectedPictureGallery.push(parentOption.defaultGalleryPicture);
        }

        this.updateGalleryPicture(parentOption);
        this.updateCalculations();
        this.selectedOptionChanged.emit(parentOption);
        this.selectionUpdated.emit(parentOption);
        return;
      }
    }

    let childOption: Option;

    if (parentOptionId === childOptionId) {
      childOption = parentOption;
    } else {
      childOption = parentOption.childOptions.filter(q => q.optionId === childOptionId)[0];

      if (!styleChoiceId) {
        parentOption.childOptions.map(q => q.isSelected = false);
        parentOption.childOptions.map(q => q.isAdded = false);
      }
    }

    if (!parentOption.isRequired && !styleChoiceId) {
      childOption.isClientSelected = true;
    }

    if (!styleChoiceId) {
      parentOption.selectedOption = childOption;
      childOption.parentOptionId = parentOption.optionId;
      if (parentOptionId !== childOptionId) {
        childOption.isSelected = true;
      }
      childOption.isAdded = true;
      this.updateCalculations();
    }

    if (styleChoiceId) {
      const styleParent = childOption.styleSelections.filter(q => q.optionId === styleParentId)[0];

      styleParent.styleChoices.map(q => q.isSelected = false);

      const styleChild = styleParent.styleChoices.filter(q => q.optionId === styleChoiceId)[0];

      styleChild.isSelected = true;
      parentOption.isStyleSelected = false;
      this.checkStyleSelections(parentOption);
    }

    if (childOption.styleSelections) {
      parentOption.styleSelections = childOption.styleSelections;
    }
    else {
      parentOption.styleSelections = null;
    }

    this.checkStyleSelections(parentOption);

    // Apply Rule on newly selected Option
    if (parentOption.selectedOption.rules && !styleChoiceId) {
      this.applyRulesForOption(parentOption.selectedOption);
    }

    // Apply Rule on old selected Option
    if (oldSelectedOption?.rules && !styleChoiceId) {
      this.applyRulesForOption(oldSelectedOption);
    }

    if (parentOption.exclusiveRules) {
      this.applyExclusiveRulesForOption(parentOption);
    }

    // Update Gallery
    parentOption.selectedPictureGallery = [];
    parentOption.selectedPictureGallery = this.getSelectedOptionPictures(parentOption);
    this.updateGalleryPicture(parentOption);

    // Apply rules
    //this.filterAreaOptions();

    // Notify subscribers that the option has been changed
    this.selectedOptionChanged.emit(parentOption);

    this.selectionUpdated.emit(parentOption);

    // Analytics (track event)
    this.$gaService.event("select_option", "selection_detail_view", parentOption.description);
  }

  checkStyleSelections(option: Option) {
    option.isStyleSelected = false;
    option.allStylesSelected = false;
    let stylesCompleted = 0;
    if (option.selectedOption) {
      if (option.selectedOption.styleSelections) {
        for (const style of option.selectedOption.styleSelections) {
          //If only one choice exists for a selection, automatically select that choice.
          if (style.styleChoices && style.styleChoices.length === 1) {
            style.styleChoices[0].isSelected = true;
            style.selectedStyle = style.styleChoices[0];
          }

          var isAnyStyleChoiceInGroupSelected = style.styleChoices.filter(q => q.isSelected).length > 0;

          for (const choice of style.styleChoices) {
            // Check for any default style selections
            if (choice.isDefault && !isAnyStyleChoiceInGroupSelected) {
              choice.isSelected = true;
              style.selectedStyle = choice;
            }

            if (choice.isSelected) {
              option.selectedOption.isStyleSelected = true;
              stylesCompleted++;
              break;
            }
            else {
              option.selectedOption.isStyleSelected = false;
            }
          }
        }
      }
    }

    if (!option?.selectedOption?.styleSelections?.length || option?.selectedOption?.styleSelections?.length === stylesCompleted) {
      option.allStylesSelected = true;
    }
  }

  getRevisedPurchasePrice(): number {
    var pricing = this.appointmentDetails.pricing;

    let total = pricing.floorplanBasePrice +
      pricing.elevationPrice +
      pricing.additionalFinanceCosts +
      pricing.changeOrders +
      pricing.homesitePremium +
      pricing.oylSiteBudget +
      pricing.structuralOptionsAtContract;

    var optionSelectionTotal = this.selectedOptions.reduce((acc, obj) => acc + (obj.price * obj.quantity), 0);

    return total + optionSelectionTotal;

  }

  updateCalculations() {
    this.selectedOptions = [];
    for (const option of this.optionDefinitions) {

      if (option.areaId > 1 && option.isVisible) {
        if (option.childOptions) {
          let childOption: Option;
          childOption = option.childOptions.find(x => (x.isSelected || x.isAdded) && option.selectedOption && option.selectedOption.price > 0);
          if (childOption) {
            this.selectedOptions.push(childOption);
          }
        }
        else {
          if ((option.isSelected || option.isAdded) && option.price > 0 && !option.isRequired) {
            this.selectedOptions.push(option);
          }
        }
      }
    }

    this.selectedOptions = this.selectedOptions.filter(x => x.price > 0);
    const totalPriceAtVds = this.selectedOptions.reduce((acc, obj,) => acc + (obj.price * obj.quantity), 0);
    this.appointmentDetails.pricing.revisedPurchasePriceVds = this.getRevisedPurchasePrice();
    this.appointmentDetails.pricing.showRoomBudgetVds = Math.abs(this.appointmentDetails.pricing.showRoomBudget - totalPriceAtVds);
    this.exceededBudgetAmount = Math.abs(this.appointmentDetails.pricing.showRoomBudget - totalPriceAtVds)
  }

  getSelectedOptionPictures(option: Option): string[] {
    const urls: string[] = [];

    if (option.pictureUrl) {
      urls.push(option.pictureUrl);
    }

    if (option.styleSelections) {
      // Get only selected styles
      const styles = option.styleSelections.filter(x => x.styleChoices.some(s => s.isSelected && s.pictureUrl)).reverse();

      for (const style of styles) {
        if (style.styleChoices && style.styleChoices.some(x => x.isSelected && x.pictureUrl)) {
          // Find selected style which have image
          let choice = style.styleChoices.find(x => x.isSelected && x.pictureUrl);
          if (choice && !urls.includes(choice.pictureUrl)) {
            urls.push(choice.pictureUrl);
          }
        }
      }
    }

    // Recursively assign child pictures
    if (option.childOptions && option.childOptions.some(x => x.isSelected || x.isAdded)) {
      let childOption = option.childOptions.find(x => x.isSelected || x.isAdded);
      if (childOption) {
        // Check for styleSelections in the childOption
        if (childOption.styleSelections) {
          for (const style of childOption.styleSelections) {
            for (const choice of style.styleChoices || []) {
              if (choice.isSelected && choice.pictureUrl && !urls.includes(choice.pictureUrl)) {
                urls.push(choice.pictureUrl);
              }
            }
          }
        }

        if (!childOption.pictureUrl) {
          childOption.pictureUrl = childOption.defaultGalleryPicture;
          childOption.defaultGalleryPictureMissing = true;
        }

        // Only add the default picture back if no styles have been selected
        if (urls.length === 0 && childOption.pictureUrl) {
          urls.push(childOption.pictureUrl);
        }

        option.isSelected = true;
      }
    }

    // If the parent option has a REAL picture
    if (option.selectedOption && !option.selectedOption.defaultGalleryPictureMissing && option.selectedOption.pictureUrl) {
      urls.push(option.selectedOption.pictureUrl);
    }

    return urls.reverse();
  }


  getFirstAvailablePictureForOption(option: Option): string {
    if (option.thumbnailUrl) { return option.thumbnailUrl; }
    else if (option.pictureUrl) { return option.pictureUrl; }
    if (option.styleSelections) {
      for (const style of option.styleSelections) {
        let styles = style.styleChoices.filter(x => x.description && x.description.trim() !== "*" && (x.thumbnailUrl || x.pictureUrl))
        styles = styles.sort((a: StyleChoice, b: StyleChoice) => {
          return a.displayName.localeCompare(b.displayName);
        });
        if (styles.length) {
          //don't use the asterisk "*" style for picture
          return styles[0].thumbnailUrl ? styles[0].thumbnailUrl : styles[0].pictureUrl;
        }
      }
    }
    if (option.childOptions) {
      for (const childOption of option.childOptions) {
        const optionPicture = this.getFirstAvailablePictureForOption(childOption);
        if (optionPicture) { return optionPicture; }
      }
    }
  }

  updateGalleryPicture(option: Option) {
    if (option.selectedPictureGallery && option.selectedPictureGallery.length) {
      option.defaultGalleryPicture = this.formatPictureUrl(option.selectedPictureGallery[0]);
      option.defaultGalleryPictureMissing = false;
      return;
    }

    if (option.isSelected && option.childOptions) {
      const selectedOptions = option.childOptions.filter(q => q.isSelected);
      if (selectedOptions?.length) {
        const selectedOption = selectedOptions[0];

        // Check for selected option and selected option styles for a picture.
        if (selectedOption.thumbnailUrl) {
          option.defaultGalleryPicture = this.getPrimaryPictureUrl(selectedOption, true);
          option.defaultGalleryPictureMissing = false;
          return;
        }
        else if (selectedOption.pictureUrl) {
          option.defaultGalleryPicture = this.getPrimaryPictureUrl(selectedOption, false);
          option.defaultGalleryPictureMissing = false;
          return;
        }

        if (selectedOption?.styleSelections?.length) {
          const selectedChoice = selectedOption?.styleSelections?.filter(q => q?.selectedStyle?.isSelected);

          if (selectedChoice?.length) {
            const choice = selectedChoice[0];
            if (choice.selectedStyle.thumbnailUrl) {
              option.defaultGalleryPicture = this.formatThumbnailPictureUrl(choice.selectedStyle.thumbnailUrl);
              option.defaultGalleryPictureMissing = false;
              return;
            }
            else if (choice.selectedStyle.pictureUrl) {
              option.defaultGalleryPicture = this.formatThumbnailPictureUrl(choice.selectedStyle.pictureUrl);
              option.defaultGalleryPictureMissing = false;
              return;
            }
          } else {
            // If no picture exists for the included feature, use the first style available
            const firstPossibleChoice = selectedOption.styleSelections[0];
            if (firstPossibleChoice?.styleChoices[0]?.mediumThumbnailUrl) {
              option.defaultGalleryPicture = this.formatThumbnailPictureUrl(firstPossibleChoice?.styleChoices[0]?.mediumThumbnailUrl);
              option.isUsingStylePlaceholder = true;
            }
          }
        }
      }
    }

    // Check for included features (not selected)
    if (!option.isSelected && option.isIncludedFeature && option.childOptions) {
      const includedFeature = option.childOptions.filter(q => q.optionId === option.includedFeatureOptionId);
      if (includedFeature?.length) {
        const selectedOption = includedFeature[0];

        if (selectedOption.thumbnailUrl) {
          option.defaultGalleryPicture = this.getPrimaryPictureUrl(selectedOption, true);
          option.defaultGalleryPictureMissing = false;
          return;
        }
        else if (selectedOption.pictureUrl) {
          option.defaultGalleryPicture = this.getPrimaryPictureUrl(selectedOption, false);
          option.defaultGalleryPictureMissing = false;
          return;
        }

        if (selectedOption?.styleSelections?.length) {
          for (const choice of selectedOption.styleSelections[0].styleChoices) {
            if (choice.thumbnailUrl) {
              option.defaultGalleryPicture = this.formatPictureUrl(choice.thumbnailUrl);
              option.defaultGalleryPictureMissing = false;
              return;
            }
            else if (choice.pictureUrl) {
              option.defaultGalleryPicture = this.formatPictureUrl(choice.pictureUrl);
              option.defaultGalleryPictureMissing = false;
              return;
            }
          }
        }
      }
    }

    // If the option is optional look for the first picture available
    if (!option.isSelected) {
      if (option.thumbnailUrl) {
        option.defaultGalleryPicture = this.getPrimaryPictureUrl(option, true);
        return;
      }
      else if (option.pictureUrl) {
        option.defaultGalleryPicture = this.getPrimaryPictureUrl(option, false);
        return;
      }

      // Check children for pictures
      if (option.childOptions && option.childOptions?.length) {
        for (const childOption of option.childOptions) {
          if (childOption.thumbnailUrl) {
            option.defaultGalleryPicture = this.getPrimaryPictureUrl(childOption, true);
            option.defaultGalleryPictureMissing = false;
            return;
          }
          else if (childOption.pictureUrl) {
            option.defaultGalleryPicture = this.getPrimaryPictureUrl(childOption, false);
            option.defaultGalleryPictureMissing = false;
            return;
          }

          // Check children styles for pictures
          if (childOption.styleSelections?.length) {
            for (const choice of childOption.styleSelections[0].styleChoices) {
              if (choice.thumbnailUrl) {
                option.defaultGalleryPicture = this.formatPictureUrl(choice.thumbnailUrl);
                option.defaultGalleryPictureMissing = false;
                return;
              }
              else if (choice.pictureUrl) {
                option.defaultGalleryPicture = this.formatPictureUrl(choice.pictureUrl);
                option.defaultGalleryPictureMissing = false;
                return;
              }
            }
          }
        }
      }
    }

    if (!option.defaultGalleryPicture && option.selectedOption) {
      option.defaultGalleryPictureMissing = true;

      const firstAvailablePicture = this.getFirstAvailablePictureForOption(option.selectedOption);

      if (firstAvailablePicture) {
        option.defaultGalleryPicture = this.formatPictureUrl(firstAvailablePicture);
      } else {
        option.defaultGalleryPicture = this.formatPictureUrl(this.getDefaultCustomPicture(option.description));
      }
    } else if (!option.defaultGalleryPicture && !option.selectedOption) {
      option.defaultGalleryPictureMissing = true;

      const firstAvailablePicture = this.getFirstAvailablePictureForOption(option);

      if (firstAvailablePicture) {
        option.defaultGalleryPicture = this.formatPictureUrl(firstAvailablePicture);
      } else {
        option.defaultGalleryPicture = this.formatPictureUrl(this.getDefaultCustomPicture(option.description));
      }
    }
    else {
      option.defaultGalleryPictureMissing = false;
      option.defaultGalleryPicture = this.formatPictureUrl(this.getDefaultCustomPicture(option.description));
    }
  }

  getPrimaryPictureUrl(option: Option, isThumbnail: boolean): string {
    if (this.mediaAssociations?.length) {
      const defaultPicture = this.mediaAssociations.filter(q => q.optionId === option.optionId && q.isPrimaryImage);
      if (defaultPicture?.length) {
        return defaultPicture[0].contentUrl;
      }
    }

    return isThumbnail ? this.formatThumbnailPictureUrl(option.thumbnailUrl) : this.formatPictureUrl(option.pictureUrl);
  }

  formatPictureUrl(relativeUrl: string) {
    // If the URL is null, simply return it.
    if (!relativeUrl) { return relativeUrl; }

    // Only add the host URL if it doesn't already include it
    if (relativeUrl.includes("http") || relativeUrl.includes("assets")) {
      return relativeUrl;
    }
    return environment.imageBaseUrl + this.encode(relativeUrl);
  }

  formatThumbnailPictureUrl(url: string) {
    // If the URL is null, simply return it.
    if (!url) { return url; }

    // Only add the host URL if it doesn't already include it
    if (url.includes("http") || url.includes("assets")) {
      return url;
    }
    return environment.thumbnailBaseUrl + this.encode(url);
  }

  getDefaultCustomPicture(description: string) {
    if (description && description.toLowerCase().includes("package")) {
      return "/assets/images/package-icon.svg";
    }

    if (description && description.toLowerCase().includes("outlet")) {
      return "/assets/images/generic/outlet.svg";
    }

    // Levels (may be removed)
    if (description && description.toLowerCase().includes("level 1")) {
      return "/assets/images/levels/level1.svg";
    }

    if (description && description.toLowerCase().includes("level 2")) {
      return "/assets/images/levels/level2.svg";
    }

    if (description && description.toLowerCase().includes("level 3")) {
      return "/assets/images/levels/level3.svg";
    }

    if (description && description.toLowerCase().includes("level 4")) {
      return "/assets/images/levels/level4.svg";
    }

    if (description && description.toLowerCase().includes("level 5")) {
      return "/assets/images/levels/level5.svg";
    }

    if (description && description.toLowerCase().includes("level 6")) {
      return "/assets/images/levels/level6.svg";
    }

    // return "/assets/images/optional.svg";
    return "/assets/images/image-unavailable.png";
  }

  getPackagePicture(optionId: number) {
    const option = this.optionDefinitions.find(x => x.optionId === optionId);
    if (option) {
      return this.getPictureForOption(option);
    }
    return "/assets/images/optional.svg";
  }

  getPictureForOption(option: Option): string {
    let picture;

    // If a custom picture has been set in VDS and assigned as the primary, override any other settings
    if (this.mediaAssociations?.length) {
      const defaultPicture = this.mediaAssociations.filter(q => q.optionId === option.optionId && q.isPrimaryImage);
      if (defaultPicture?.length) {
        picture = defaultPicture[0].contentUrl;
        return picture;
      }
    }

    picture = this.getFirstAvailablePictureForOption(option);

    if (picture) {
      return picture;
    }

    return this.getDefaultCustomPicture(option.description);
  }

  getPictureForStyleChoice(styleChoice: StyleChoice): string {
    if (styleChoice.pictureUrl) {
      return styleChoice.pictureUrl;
    }

    return this.getDefaultCustomPicture(styleChoice.description);
  }

  // This method is for the demo.  Excluded options shoudln't even be returned by the platform.
  isExcluded(optionId: number): boolean {
    const exclusions: number[] = [];

    if (exclusions.some(q => q === optionId)) {
      return true;
    }

    return false;
  }

  prepareOptionPictures(option: Option) {
    if (option.pictureUrl) {
      option.pictureUrl = this.formatPictureUrl(option.pictureUrl);
    }

    if (option.thumbnailUrl) {
      option.thumbnailUrl = this.formatPictureUrl(option.thumbnailUrl);
    }

    if (!option.defaultGalleryPicture && option.pictureUrl) {
      option.defaultGalleryPicture = option.pictureUrl;
    }

    if (option?.styleSelections?.length) {
      for (const style of option.styleSelections) {
        if (style?.styleChoices.length) {
          for (const choice of style.styleChoices) {
            if (choice.pictureUrl) {
              choice.pictureUrl = this.formatPictureUrl(choice.pictureUrl);
            }

            if (choice.thumbnailUrl) {
              choice.thumbnailUrl = this.formatPictureUrl(choice.thumbnailUrl);
            }

            if (choice.mediumThumbnailUrl) {
              choice.mediumThumbnailUrl = this.formatPictureUrl(choice.mediumThumbnailUrl);
            }
          }
        }
      }
    }

    if (option.childOptions) {
      for (const childOption of option.childOptions) {
        this.prepareOptionPictures(childOption);
      }
    }
  }

  prepareOption(option: Option, skipPictureInitalization = false) {

    if (option.prepared) { return; }
    option.prepared = true;

    if (!option.price) { option.price = 0; }

    // Fix picture references
    if (!skipPictureInitalization) {
      this.prepareOptionPictures(option);
    }

    if (option?.tags?.length) {
      for (let tag of option.tags) {
        if (tag.startsWith("PIN ") || tag.startsWith("PIN")) {
          option.isPinnable = true;
        }
      }
    }

    if (!option.isSelected && (option.isHiddenByDefault || option.isHiddenByRule)) {
      option.isVisible = false;
      option.isAdded = false;
    } else {
      option.isVisible = true;
    }

    if (option.isRequired &&
      !option.isHiddenByDefault &&
      !option.isHidden &&
      !option?.childOptions?.length &&
      option.price === 0 &&
      !option?.parentOptionId) {
      option.isSelected = true;
      option.isIncludedFeature = true;
      option.selectedOption = option;
    }

    if (!option.isIncludedFeature && option.price === 0) {
      option.isIncludedFeature = true;
    }

    if (!option.isSelected && (option.isHiddenByRule)) {
      option.isVisible = false;
      option.isAdded = false;
    } else {
      option.isVisible = true;
    }
    let isAdmin = JSON.parse(localStorage.getItem('userType')).isAdmin;
    if (!option.isSelected && (option.isHiddenByDefault || option.isHiddenByRule || isAdmin)) {
      option.isVisible = false;
      option.isAdded = false;
    } else {
      option.isVisible = true;
    }

    // Quantity should always be at least 1.
    if (!option.quantity) { option.quantity = 1; }
    option.oldQuantity = option.quantity;

    // Tag any options as inspired (if selected in an inspiration room)
    if (this.inspirationOptionIds
      && this.inspirationOptionIds.includes(option.optionId)) {
      option.isLiked = true;
    }

    // Display Name
    if (!option.displayName) {
      option.displayName = option.description;
    }

    // Temporary - may need an intelligent cleaning engine
    option.description = this.cleanVerbiage(option.description);

    // Temporary collection to hold unique styles (hot fix)
    let styleSelections: StyleSelection[] = [];

    if (option.styleSelections) {
      for (const style of option.styleSelections) {
        if (!style) {
          continue;
        }

        if (styleSelections.filter(q => q.optionId === style.optionId).length) {
          // Duplicate style
          console.log(`Skipped duplicate style: ${style.optionId}`);
          continue;
        }

        style.description = this.cleanVerbiage(style.description, true);

        //If only one choice exists for a selection, automatically select that choice.
        if (style.styleChoices && style.styleChoices.length === 1) {
          style.styleChoices[0].isSelected = true;
          style.selectedStyle = style.styleChoices[0];
        }

        var isAnyStyleChoiceInGroupSelected = style.styleChoices.filter(q => q.isSelected).length > 0;

        for (const choice of style.styleChoices) {
          choice.description = this.cleanVerbiage(choice.description, true);
          if (choice.isDefault && !isAnyStyleChoiceInGroupSelected) {
            choice.isSelected = true;
            style.selectedStyle = choice;
          }
        }


        style.styleChoices = style.styleChoices.filter(x => !x.isRemoved);
        styleSelections.push(style);
      }

      if (styleSelections.length) {
        option.styleSelections = styleSelections;
      }
    }

    if (option.isSelected || option.isAdded || (option.childOptions && option.childOptions.some(x => x.isAdded))) {
      if (option.childOptions) {
        var selectedChild = option.childOptions.find(x => x.isSelected || x.isAdded)
        if (selectedChild) {
          option.price = selectedChild.price;
          option.selectedOption = selectedChild;
          if (!option.styleSelections && (selectedChild.styleSelections)) {
            option.styleSelections = selectedChild.styleSelections;
          }
          option.selectedPictureGallery = [];
          option.selectedPictureGallery = this.getSelectedOptionPictures(option);
        }

        if (!option.selectedOption) {
          var defaultOption = option.childOptions.find(x => x.optionId == option.includedFeatureOptionId);
          if (defaultOption) {
            option.isSelected = true;
            defaultOption.isSelected = true;
            option.selectedOption = defaultOption;
          }
        }
      } else {
        option.selectedOption = option;
      }
    }

    // Only set the default gallery picture on top-level options
    if (!option.defaultGalleryPicture) {
      // Set default picture for gallery
      this.updateGalleryPicture(option);
    }

    if (option.childOptions) {
      for (const childOption of option.childOptions) {
        this.prepareOption(childOption, true);
      }
    }
  }

  // We need some kind of verbiage cleaning engine
  cleanVerbiage(description: string, isSubOption = false): string {
    if (!description) { return ""; };
    description = description.trim();

    return description;
  }

  prepareAllOptionsForSelection() {
    for (const option of this.optionDefinitions) {
      if (Array.isArray(option.childOptions)) {
        for (const childOption of option.childOptions) {
          if (childOption.isSelected) {
            option.isSelected = true;
            break;
          }
        }
      }

      option.selectedPictureGallery = [];

      this.prepareOption(option);

      if (option.isSelected) {
        option.selectedPictureGallery.push(...this.getSelectedOptionPictures(option));
      }
    }
  }


  getSelectedOrDefault(options: Option[]): Option {
    const option = options.filter(q => q.isSelected);

    let selectedOption: Option;

    // First attempt to find the first instance of a selected option for this group
    if (option && option.length) {
      selectedOption = option[0];
    }

    if (selectedOption && selectedOption.childOptions) {
      const selectedChild = selectedOption.childOptions.filter(q => q.isSelected);

      if (selectedChild && selectedChild.length) {
        selectedOption = selectedChild[0];
      }
    }

    return selectedOption;
  }

  getSelectedOption(parentOption: Option) {
    if (parentOption.childOptions) {
      for (const option of parentOption.childOptions) {
        if (option.isSelected) {
          return option;
        }
      }
    }
  }

  getSelectedStyleChoice(styleParent: StyleSelection) {
    if (styleParent.styleChoices) {
      for (const choice of styleParent.styleChoices) {
        if (choice.isSelected) {
          return choice;
        }
      }
    }
  }

  optionsByArea(areaId: number, areas: Area[], tag: string): Option[] {
    if (areaId) {
      const area = areas.filter(q => q.id === areaId);
      if (area) {
        const subAreas = area[0]?.subAreas;
        if (subAreas) {
          let displayedOptions = this.displayedOptions;

          if (area[0].name === "Value Packages") {
            tag = null;
          }

          displayedOptions = this.displayedOptions.filter(q => subAreas.map((s) => { return s.id }).includes(q.areaId));
          displayedOptions = displayedOptions.filter(q => (q.isSelected && q.isVisible) || (q.isRequired && q.isVisible));

          if (tag && tag != "Other Options" && this.optionTags) {
            this.getTags(displayedOptions, tag);
            displayedOptions = displayedOptions.filter(x => x.tag === tag);
          }

          if (tag && tag === "Other Options" && this.optionTags) {
            displayedOptions = displayedOptions.filter(x => !x.hasOwnProperty('tag'));
          }
          return displayedOptions;
          //const sortedOptions = displayedOptions.sort((a, b) => +a.defaultGalleryPictureMissing - +b.defaultGalleryPictureMissing);
          //return sortedOptions;
        }
      }
    }
    const displayedOptions = this.displayedOptions.filter(q => q.isSelected && q.selectedOption && q.isVisible);
    return displayedOptions.sort((a, b) => (a.areaId > b.areaId) ? 1 : -1);
  }

  optionalOptionsByArea(areaId: number, areas: Area[], tag: string): Option[] {
    if (areaId) {
      const area = areas.filter(q => q.id === areaId);
      if (area) {
        const subAreas = area[0]?.subAreas;

        let optionalOptions = this.optionDefinitions.filter(option => !option.isRequired && !option.isSelected && option.isVisible);

        optionalOptions = optionalOptions.filter(q => subAreas.map((s) => { return s.id }).includes(q.areaId));

        if (tag && tag !== "Other Options" && this.optionTags && area[0].name !== "Value Packages") {
          this.getTags(optionalOptions, tag);
          optionalOptions = optionalOptions.filter(x => x.tag === tag);
        }
        if (tag && tag === "Other Options" && this.optionTags && area[0].name !== "Value Packages") {
          optionalOptions = optionalOptions.filter(x => !x.hasOwnProperty('tag'));
        }

        const sortedOptionsList = optionalOptions.filter(x => x.selectedOption).concat(optionalOptions.filter(x => !x.selectedOption))

        return sortedOptionsList;

        //const sortedOptional = (optionalOptions.sort((a, b) => +a.defaultGalleryPictureMissing - +b.defaultGalleryPictureMissing)).sort((c, d) => +d.isClientSelected - +c.isClientSelected);
        //return sortedOptional;
      }
    }
    return null;
  }

  getTags(displayedOptions: Option[], selectedTag) {
    const records = [];
    const displayedOptionIds = displayedOptions.map(({ optionId }) => optionId.toString());
    for (const displayedOptionId of displayedOptionIds) {
      const tagData = this.optionTags.filter(x => x.optionIds.includes(+displayedOptionId) && (x.displayName === selectedTag || x.tagName === selectedTag));

      const option = displayedOptions.filter(x => x.optionId.toString() === displayedOptionId)[0];

      if (tagData.length > 0) {
        option.tag = selectedTag;
        records.push(tagData);
      }
    }
    return records;
  }

  baseAreaOptions(): Option[] {
    const baseOptions = this.optionDefinitions.filter(option => option.isRequired || option.isSelected || option.isVisible);

    //let o = baseOptions.find(q => q.optionId === 1877);

    for (const option of baseOptions) {
      //option.isVisible = option.isSelected;

      /* if (option.isRequired) {*/
      //option.isSelected = true;

      if (option.childOptions) {
        // If the option already has a user selection, don't look for an included feature
        const preexistingSelection = option.childOptions.filter(q => q.isSelected);

        if (preexistingSelection) {
          continue;
        }

        for (const child of option.childOptions) {
          if (option.includedFeatureOptionId === child.optionId) {
            child.isSelected = true;
            break;
          }
        }
      }
    }
    //}

    return baseOptions;
  }

  selectionTotal(): number {
    const baseOptions = this.optionDefinitions.filter(option => option.isSelected);

    let optionPricing = 0;

    for (const option of baseOptions) {
      if (option.price) {
        optionPricing += option.price;
        continue;
      }

      if (option.childOptions) {
        for (const child of option.childOptions) {
          if (child.isSelected && child.price) {
            optionPricing += child.price;
            break;
          }
        }
      }
    }
    return optionPricing;
  }

  encode(imageUrl: string): string {
    imageUrl = imageUrl.replace(/\(/g, '%28');
    imageUrl = imageUrl.replace(/\)/g, '%29');
    return imageUrl;
  }

  formatPrice(val: number) {
    if (!val) { val = 0; }
    return new Intl.NumberFormat('en-US',
      { style: 'currency', currency: 'USD' }
    ).format(val);
  }

  showRemoveButton(option: Option) {
    if (option.childOptions && option.childOptions.length === 1) {
      if (option.childOptions[0].isAdded) {
        return true;
      }
    }
    if (!option.childOptions && option.isAdded) {
      return true;
    }
    return false;
  }

  resetToIncludedFeature(option: Option) {
    if (option.isRequired) {
      option.quantity = 1;

      if (!option.includedFeatureOptionId) {
        this.selectOption(option.optionId, option.selectedOption.optionId, undefined, undefined, false)
      }

      if (option?.selectedOption.optionId !== option.includedFeatureOptionId) {
        this.selectOption(option.optionId, option.includedFeatureOptionId, undefined, undefined, false);
      } else {
        this.resetStyles(option.optionId);
      }
    }
    else {
      this.selectOption(option.optionId, option.selectedOption.optionId, undefined, undefined, false);
    }
  }

  requiresStyleSelections(option: Option): boolean {
    //If an option is required but no option/subOption is selected
    if (option.isRequired && !option.selectedOption) {
      return true;
    }

    if (!option?.selectedOption?.styleSelections) { return false; }

    const selectionsNeeded = option.selectedOption.styleSelections.length;

    let selectionsMade = 0;

    if (option.selectedOption.styleSelections) {
      for (const style of option.selectedOption.styleSelections) {
        for (const choice of style.styleChoices) {
          if (choice.isSelected) { selectionsMade++; }
        }
      }
    }

    return selectionsNeeded > selectionsMade;
  }

  setDirty() {
    this.isDirty = true;
    //mark DirtyWhileSaving true, if saving is in progress and in between, the user done some changes.
    if (this.isSaving) {
      this.isDirtyWhileSaving = true;
    }
  }
}
